import { useState, useEffect, useRef, useMemo } from "react";
import { NAVY } from "./styles/palette";
import { globalCss } from "./styles/global";
import { SUPABASE_URL, SUPABASE_ANON_KEY, sb, sbAuth, getAuthToken, refreshSession } from "./lib/supabase";
import { getPrice, getDeoMonthly } from "./lib/pricing";
import { localDateStr } from "./lib/date";
import { setDynamicZipDays, DAYS } from "./lib/serviceArea";
import { configureNavStack, navHandleBack, navReset, useBackClose } from "./lib/navStack";
import { CommsContext } from "./lib/commsReady";
import { Icon } from "./components/Icon";
import { ScoopMark } from "./components/ScoopMark";
import { OnboardingWizard } from "./components/OnboardingWizard";
import { RefundModal } from "./components/RefundModal";
import { ReportsPage } from "./components/ReportsPage";
import { FleetPage } from "./components/FleetPage";
import { TechProfilesPage } from "./components/TechProfilesPage";
import { SettingsPage } from "./components/SettingsPage";
import { AuthPage } from "./components/AuthPage";
import { Dashboard } from "./components/Dashboard";
import { Clients } from "./components/Clients";
import { Leads } from "./components/Leads";
import { Softphone } from "./components/Softphone";
import { Schedule } from "./components/Schedule";
import { TechView } from "./components/TechView";
import { Route } from "./components/Route";
import { Invoices } from "./components/Invoices";
import { ClientMap } from "./components/ClientMap";
import { ClientProfile } from "./components/ClientProfile";
import { RequestsPage } from "./components/RequestsPage";
import { PlatformPage } from "./components/PlatformPage";
import { DeliveriesPage } from "./components/DeliveriesPage";
import { ModerationPage } from "./components/ModerationPage";
import { PicksPage } from "./components/PicksPage";
import { ManualJobModal } from "./components/ManualJobModal";
import { ClientModal } from "./components/ClientModal";

// ═══════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════
export default function App() {
  const [page,       setPage]       = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useBackClose(sidebarOpen, () => setSidebarOpen(false)); // Back/swipe-back closes the drawer first // mobile nav drawer
  // Browser back/forward moves between tabs: push a history entry on each tab change and
  // restore the tab on popstate. In-page overlays (modals/drawers) opt into navStack so Back
  // closes the open overlay first and only leaves the tab once nothing is open.
  const _navPopping = useRef(false);
  const _navFirst = useRef(true);
  const _pageRef = useRef(page);
  _pageRef.current = page;
  useEffect(() => { configureNavStack(() => _pageRef.current); }, []);
  useEffect(() => {
    navReset(); // tab changed → overlays from the old page are gone
    if (_navPopping.current) { _navPopping.current = false; return; }
    if (_navFirst.current) { _navFirst.current = false; window.history.replaceState({ page }, ""); return; }
    window.history.pushState({ page }, "");
  }, [page]);
  useEffect(() => {
    const onPop = (e) => {
      if (navHandleBack()) return; // Back closed an in-page overlay — stay on this tab
      _navPopping.current = true;
      setPage((e.state && e.state.page) || "dashboard");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [clients,    setClients]    = useState([]);
  const [leads,      setLeads]      = useState([]);
  const [requests,   setRequests]   = useState([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [invoices,   setInvoices]   = useState([]);

  // Auto-sync open invoices from Stripe — runs when page or profile changes
  async function syncOpenInvoices(clientId) {
    try {
      const body = clientId ? { client_id: clientId } : {};
      const res = await fetch(`${SUPABASE_URL}/functions/v1/sync-invoices`, {
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify(body),
      }).then(r=>r.json());
      if (res.synced > 0) {
        const fresh = await fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
        }).then(r=>r.json());
        if (Array.isArray(fresh)) setInvoices(fresh);
      }
    } catch(e) { /* silent fail */ }
  }
  const [jobsDone,   setJobsDone]   = useState({});

  // Load today's completed visits from DB to sync jobsDone with source of truth
  async function refreshJobsDone() {
    const todayStr = localDateStr();
    const res = await fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${todayStr}&select=id,client_id,status,service_type`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json());
    if (Array.isArray(res)) {
      const done = {};
      res.forEach(j=>{ if(j.status==="completed") done[j.id]=true; });
      setJobsDone(done);
    }
  }

  // C4/B4: one shared toggle. Derive the next state inside the functional
  // updater (so two rapid clicks don't both read a stale closure), guard against
  // a re-entrant PATCH for the same id, clear started_at on un-complete (else the
  // next completion computes a bogus duration), and reconcile with the DB after.
  const toggleInFlight = useRef(new Set());
  async function toggleJob(id) {
    if (toggleInFlight.current.has(id)) return;
    // Manual jobs are billed at CREATION, so completing one is a plain status
    // flip just like any other job — no charge branch here.
    toggleInFlight.current.add(id);
    let nextDone = false;
    setJobsDone(d => { nextDone = !d[id]; return { ...d, [id]: nextDone }; });
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${id}`,{
        method:"PATCH",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
        body:JSON.stringify(nextDone
          ? { status:"completed", completed_at:new Date().toISOString() }
          : { status:"scheduled", completed_at:null, started_at:null }),
      });
    } finally {
      toggleInFlight.current.delete(id);
      refreshJobsDone();
    }
  }

  const [weekOffset, setWeekOffset] = useState(0);
  const [scheduleMonthOffset, setScheduleMonthOffset] = useState(0); // C3: visible schedule month (lifted from Schedule)
  const [modal,      setModal]      = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [convertingLead, setConvertingLead] = useState(null); // lead being converted → prefills New-client modal
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState(null);
  const [hovered,    setHovered]    = useState(null);
  const [profileClient, setProfileClient] = useState(null);
  const [mapFilter,    setMapFilter]    = useState("all");

  const [session,    setSession]    = useState(null);
  const [authReady,  setAuthReady]  = useState(false);
  const [userRole,   setUserRole]   = useState("owner"); // owner | technician
  const [userTechId, setUserTechId] = useState(null);
  const [org,        setOrg]        = useState(null); // current operator's organization row

  // Load the logged-in operator's organization (RLS returns only their own).
  // Used to decide whether to show the onboarding wizard (Phase C).
  async function fetchOrg() {
    try {
      const rows = await fetch(`${SUPABASE_URL}/rest/v1/organizations?select=*`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` },
      }).then(r => r.json());
      setOrg(Array.isArray(rows) ? rows[0] || null : null);
    } catch (e) { console.error("fetchOrg error:", e); }
    // Is this login also the PLATFORM owner? Asked of the table (is_platform_admin),
    // never of the JWT claim, so revoking it takes effect on the next page load rather
    // than whenever the token happens to refresh.
    try {
      const yes = await fetch(`${SUPABASE_URL}/rest/v1/rpc/is_platform_admin`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: "{}",
      }).then(r => (r.ok ? r.json() : false));
      setIsPlatformAdmin(yes === true);
    } catch { setIsPlatformAdmin(false); }
  }
  const [showNotifs,   setShowNotifs]   = useState(false);
  const [blockOffDates, setBlockOffDates] = useState([]); // eslint-disable-line no-unused-vars
  const [readNotifs, setReadNotifs] = useState(() => { try { return JSON.parse(localStorage.getItem("readNotifs")||"[]"); } catch(e) { return []; } });
  const [manualJobs,      setManualJobs]      = useState([]);
  const [jobs,            setJobs]            = useState([]);
  const [pricing,         setPricing]         = useState(null); // null = use hardcoded fallback
  const [dbVehicles,      setDbVehicles]      = useState([]);
  const [dbTrips,         setDbTrips]         = useState([]);
  const [jobsLoading,     setJobsLoading]     = useState(false);
  const [showManualJob,   setShowManualJob]   = useState(false);
  const [manualJobClient, setManualJobClient] = useState(null);
  const [technicians,     setTechnicians]     = useState([]);
  const [refunds,         setRefunds]         = useState([]);
  const [credits,         setCredits]         = useState([]);
  const [serviceArea,     setServiceArea]     = useState({}); // { day: [zips] }
  // { zip: second_day|null } — the OPTIONAL second service day a twice-weekly client
  // gets. Kept separate from serviceArea because that map is keyed by day, which loses
  // it. Without a second day the generator schedules only the primary, so the Schedule
  // tab has to be able to say which clients are quietly getting half their visits.
  const [zipSecondDay,    setZipSecondDay]    = useState({});
  const [showRefundModal, setShowRefundModal] = useState(null); // invoice obj
  const [comms, setComms] = useState({ textingReady: true, fleetReady: true, number: null }); // optimistic until loaded
  const [settingsJump, setSettingsJump] = useState(null); // deep-link a guide → a Settings tab

  // Browser Back closes whichever overlay is open before it leaves the tab (see navStack).
  useBackClose(!!modal, () => { setModal(null); setEditTarget(null); setConvertingLead(null); });
  useBackClose(!!showRefundModal, () => setShowRefundModal(null));
  useBackClose(showManualJob, () => { setShowManualJob(false); setManualJobClient(null); });
  useBackClose(showNotifs, () => setShowNotifs(false));

  // Gated on session so we don't fire with a stale/missing token at mount.
  useEffect(()=>{ if (session) refreshJobsDone(); },[session]); // eslint-disable-line react-hooks/exhaustive-deps
  // Integration readiness (texting/fleet) → gates Call/Text/Fleet and guides to setup. Reload when
  // the org changes (e.g. after connecting Bouncie or getting a number).
  useEffect(()=>{ if (!session) return; sb.fn("comms-readiness", {}).then(r=>{ if (r && typeof r.textingReady === "boolean") setComms(r); }).catch(()=>{}); },[session, org?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync open invoices when navigating to invoices page
  useEffect(()=>{
    if (session && page==="invoices") syncOpenInvoices(null);
  },[page, session]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  // Central data refresh
  async function refreshData(silent=false) {
    if (!silent) setLoading(true);
    try {
      const [c, inv, mj, techs, refs, creds, lds, reqs] = await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/jobs?source=eq.manual&order=scheduled_date.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/technicians?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/refunds?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/credits?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/leads?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/requests?order=created_at.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()),
      ]);
      if (Array.isArray(c))     setClients(c);
      if (Array.isArray(inv))   setInvoices(inv);
      // Manual jobs are jobs-table rows (source='manual'). Map scheduled_date→date
      // so the existing manual-job lists/Route (which read j.date) keep working.
      if (Array.isArray(mj))    setManualJobs(mj.map(j=>({ ...j, date: j.scheduled_date })));
      if (Array.isArray(techs)) setTechnicians(techs);
      if (Array.isArray(refs))  setRefunds(refs);
      if (Array.isArray(creds)) setCredits(creds);
      if (Array.isArray(lds))   setLeads(lds);
      if (Array.isArray(reqs))  setRequests(reqs);
      // Load service area zip mappings
      const sa = await fetch(`${SUPABASE_URL}/rest/v1/service_area`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(sa) && sa.length > 0) {
        const mapped = {};
        const seconds = {};
        sa.forEach(r => {
          if (!mapped[r.day]) mapped[r.day]=[];
          mapped[r.day].push(r.zip);
          seconds[r.zip] = r.second_day || null;
        });
        setServiceArea(mapped);
        setZipSecondDay(seconds);
      }
      const bd = await fetch(`${SUPABASE_URL}/rest/v1/block_off_dates?order=date.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(bd)) setBlockOffDates(bd);

      // Load current month trips for tech stats
      const tripsNow = new Date();
      const tripMonthFrom = `${tripsNow.getFullYear()}-${String(tripsNow.getMonth()+1).padStart(2,"0")}-01`;
      const tripRows = await fetch(`${SUPABASE_URL}/rest/v1/trips?start_time=gte.${tripMonthFrom}&select=id,imei,technician_id,start_time,end_time,distance_miles,trip_type,time_at_location_mins,reimbursement_amount`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json());
      if (Array.isArray(tripRows)) setDbTrips(tripRows);

      // Sync vehicles from Bouncie to DB then load
      try {
        await fetch(`${SUPABASE_URL}/functions/v1/bouncie-proxy?endpoint=/vehicles`,{
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
        });
      } catch(e) { console.log("Bouncie vehicle sync skipped:", e.message); }
      const veh = await fetch(`${SUPABASE_URL}/rest/v1/vehicles?order=nickname.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(veh)) setDbVehicles(veh);

      // Trip sync now runs on a 2-minute interval (see useEffect below)
      // so it picks up trips Bouncie finalizes mid-session, not just at load.

      // Load pricing from service_prices table
      const sp = await fetch(`${SUPABASE_URL}/rest/v1/service_prices`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(sp) && sp.length > 0) {
        const monthly = {};
        const perVisit = {};
        const deoPerVisit = {};
        sp.forEach((row) => {
          if (row.service === "cleanup") {
            if (!monthly[row.frequency]) monthly[row.frequency] = {};
            monthly[row.frequency][row.dogs] = Number(row.price);
            // Per visit = monthly / avg visits per month
            const visitsPerMonth = { weekly:4.33, biweekly:2.17, "twice-weekly":8.66 };
            if (!perVisit[row.frequency]) perVisit[row.frequency] = {};
            perVisit[row.frequency][row.dogs] = Math.round((Number(row.price) / (visitsPerMonth[row.frequency]||4.33)) * 100) / 100;
          } else if (row.service === "deo") {
            deoPerVisit[row.frequency] = Number(row.price);
          }
        });
        setPricing({ monthly, perVisit, deoPerVisit });
      }

      // Load jobs for current and next month
      const now2     = new Date();
      const fromDate = `${now2.getFullYear()}-${String(now2.getMonth()+1).padStart(2,"0")}-01`;
      const toDate   = new Date(now2.getFullYear(), now2.getMonth()+2, 0).toISOString().split("T")[0];
      const jobsRes  = await fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=gte.${fromDate}&scheduled_date=lte.${toDate}&order=scheduled_date.asc&limit=2000`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json());
      if (Array.isArray(jobsRes)) setJobs(jobsRes);
    } catch(e) { console.error("Refresh error:", e); }
    if (!silent) setLoading(false);
  }

  // C3/C5: load jobs for an arbitrary month range and MERGE by id (so navigating
  // the calendar outside the base current+next-month window actually fetches that
  // month instead of rendering an empty grid). Used on month-nav and after a
  // generate for a non-loaded month.
  async function loadJobsForRange(from, to) {
    const rows = await fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=gte.${from}&scheduled_date=lte.${to}&order=scheduled_date.asc&limit=5000`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json()).catch(()=>null);
    if (!Array.isArray(rows)) return;
    setJobs(prev => {
      const map = new Map(prev.map(j=>[j.id,j]));
      rows.forEach(j=>map.set(j.id,j));
      return Array.from(map.values());
    });
  }

  // When the schedule month moves outside the base window, fetch that month.
  useEffect(() => {
    if (!session || scheduleMonthOffset === 0 || scheduleMonthOffset === 1) return; // base load covers current+next
    const base   = new Date();
    const mStart = new Date(base.getFullYear(), base.getMonth()+scheduleMonthOffset, 1);
    const mEnd   = new Date(base.getFullYear(), base.getMonth()+scheduleMonthOffset+1, 0);
    loadJobsForRange(localDateStr(mStart), localDateStr(mEnd));
  }, [scheduleMonthOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for existing session on mount
  useEffect(() => {
    sbAuth.getSession().then(s => {
      setSession(s);
      if (s) { setUserRole(s.role||"owner"); setUserTechId(s.technician_id||null); }
      setAuthReady(true);
      if (s) { refreshData(); fetchOrg(); }
      else setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the access token fresh while the app stays open. Tokens expire (~1h) and the
  // Fleet auto-refresh + trip sync now require a valid JWT, so a stale token would
  // silently 401 and freeze the data. Refresh every 40 min while logged in.
  useEffect(() => {
    if (!session) return;
    const id = setInterval(() => { refreshSession(); }, 40 * 60 * 1000);
    return () => clearInterval(id);
  }, [session]);

  // Refresh data every time the user switches pages
  useEffect(() => {
    if (session && page !== "dashboard") refreshData(true);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync DB-loaded serviceArea into the lib/serviceArea module so
  // getDayForZip() picks it up everywhere without prop-drilling.
  useEffect(() => {
    if (Object.keys(serviceArea).length > 0) {
      setDynamicZipDays(serviceArea);
    }
  }, [serviceArea]);

  // Bouncie trip sync + wake-up handling. Runs when session is ready and
  // then on three triggers:
  //   - Every 2 minutes (setInterval) — picks up trips Bouncie finalizes
  //     while you're actively using the app
  //   - On visibilitychange when the tab returns to visible — covers
  //     laptop-wake, tab-switch-back, browser-restore. setInterval is
  //     paused while the laptop is asleep, so this is the actual fix for
  //     "left the tab open for hours, came back, nothing updated"
  //   - On window focus — backup for browsers that don't fire
  //     visibilitychange reliably (older Safari)
  // Also re-validates the Supabase session on wake-up — if the JWT has
  // expired during sleep, kicks the user back to the login screen
  // instead of silently failing every subsequent fetch.
  useEffect(() => {
    if (!session) return;
    let alive = true;

    // New self-serve signups land in the DB with no signal to an already-open app —
    // the operator's list (and map) simply never shows them until a full reload,
    // which reads as "she's active but not on the client list". Ride the existing
    // 2-minute/wake-up cadence with one cheap REST read (own Supabase, no Google/
    // Stripe calls) so new clients appear within ~2 min.
    async function refreshClientsQuietly() {
      try {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } });
        if (r.status === 401 || r.status === 403) { await handleExpiredSession(); return; }
        const rows = await r.json();
        if (Array.isArray(rows) && alive) setClients(rows);
      } catch (e) { /* silent — next trigger will retry */ }
    }

    async function syncTripsAndReload() {
      refreshClientsQuietly();   // fire-and-forget alongside the trips sync
      try {
        const r = await fetch(`${SUPABASE_URL}/functions/v1/sync-bouncie-trips`, {
          method: "POST",
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
          body: JSON.stringify({ end_date: new Date().toISOString() }),
        });
        if (r.status === 401 || r.status === 403) { await handleExpiredSession(); return; }
        const res = await r.json();
        if (res?.total_synced > 0 && alive) {
          const now = new Date();
          const monthFrom = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
          const tripsRes = await fetch(
            `${SUPABASE_URL}/rest/v1/trips?start_time=gte.${monthFrom}&select=id,imei,technician_id,start_time,end_time,distance_miles,trip_type,time_at_location_mins,reimbursement_amount`,
            { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }
          );
          if (tripsRes.status === 401 || tripsRes.status === 403) { await handleExpiredSession(); return; }
          const rows = await tripsRes.json();
          if (Array.isArray(rows) && alive) setDbTrips(rows);
        }
      } catch (e) { /* silent — next trigger will retry */ }
    }

    // If a fetch sees 401/403, the JWT probably expired during sleep.
    // Re-validate via sbAuth (which calls /auth/v1/user with the stored
    // token); if that comes back null, the session is dead — clear it so
    // App re-renders the AuthPage.
    async function handleExpiredSession() {
      try {
        const fresh = await sbAuth.getSession();
        if (!fresh && alive) {
          setSession(null);
          setUserRole("owner");
          setUserTechId(null);
        }
      } catch (e) { /* if even revalidation fails, leave state alone */ }
    }

    // Fired by both visibilitychange and focus — re-validate session,
    // then sync trips. Cheap to call: getSession is one /auth/v1/user
    // request, which short-circuits if the JWT is still valid.
    async function onWakeUp() {
      if (document.hidden) return; // visibilitychange fires for both directions
      await handleExpiredSession();
      // handleExpiredSession may have set session to null; the next
      // effect run (deps=[session]) will then tear this listener down.
      syncTripsAndReload();
    }

    syncTripsAndReload();
    const iv = setInterval(syncTripsAndReload, 120000); // 2 minutes
    document.addEventListener("visibilitychange", onWakeUp);
    window.addEventListener("focus", onWakeUp);
    return () => {
      alive = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onWakeUp);
      window.removeEventListener("focus", onWakeUp);
    };
  }, [session]);

  const today     = new Date();
  // eslint-disable-next-line no-unused-vars
  const todayName = DAYS[today.getDay()];
  // todayJobs and allJobs now derived from jobs table (source of truth).
  // C1: civil "today" in LOCAL time, not toISOString() (UTC), which rolls over
  // to tomorrow after ~7pm ET and desyncs route/overdue/completion lookups.
  const todayStr  = localDateStr(today);
  // MEMOISED ON PURPOSE — COSTS REAL MONEY. A bare .filter() here returns a NEW array on
  // every App render, which cascades: Route.js re-memoises techGroups -> TechCard re-memoises
  // `routable` -> its road-matrix useEffect (keyed on that array's identity) refires and calls
  // the Google Routes API again. That API bills PER ORIGIN-DESTINATION PAIR, so one tech with
  // 15 stops is a 16x16 = 256-element charge every single time. The 2-minute Bouncie poll
  // (setInterval below) alone would have re-billed the whole team's matrices all day.
  const todayJobs = useMemo(
    () => jobs.filter(j => j.scheduled_date === todayStr && j.status !== "rescheduled"),
    [jobs, todayStr],
  );
  const allJobs   = jobs; // kept for legacy Schedule prop
  // I2: MRR must include the separately-billed deodorizer sub for clients with
  // an ACTIVE deo (paused/cancelled deo excluded), or recurring revenue is understated.
  const mrr       = clients.filter(c=>c.active).reduce((s,c)=>
    s + getPrice(c.frequency,c.dogs,pricing)
      + (c.deo_enabled && c.deo_status==="active" ? getDeoMonthly(c.deo_frequency, pricing) : 0), 0);

  async function handleSaveClient(data) {
    setLoading(true);
    try {
      if (editTarget) {
        // CONTACT-ONLY edit. NEVER PATCH plan/deo/billing fields here — those alter
        // Stripe and must go through the profile (update-subscription / schedule_change);
        // a raw PATCH of deo_enabled/frequency/dogs would silently drift from Stripe.
        const CONTACT_FIELDS = ["first_name","last_name","email","phone","phone_secondary","address","city","state","zip","lat","lng","location_source","location_type","notes"];
        const body = {}; for (const k of CONTACT_FIELDS) if (k in data) body[k] = data[k];
        await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${editTarget.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify(body)});
        showToast("Client updated");
      } else {
        showToast("Client added & billing set up!");
        // Silently re-match trips in background — new client may match existing trips
        (async()=>{
          let offset = 0; let done = false;
          while (!done) {
            const res = await fetch(`${SUPABASE_URL}/functions/v1/rematch-trips`,{
              method:"POST",
              headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
              body:JSON.stringify({ offset })
            }).then(r=>r.json()).catch(()=>({ done:true }));
            offset = res.offset || 0; done = res.done || true;
          }
        })();
      }
      const fresh = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(fresh)) setClients(fresh);

      // If this NEW client came from converting a lead, mark the lead converted
      // and link it to the new client. create-customer returns the client row as
      // `data` (with .id); fall back to matching the lead by phone/email in the
      // freshly-loaded list. Scoped by lead id under the operator JWT (RLS).
      if (!editTarget && convertingLead) {
        const leadDigits = (convertingLead.phone || "").replace(/\D/g, "");
        let newClientId = data?.id ?? null;
        if (!newClientId && Array.isArray(fresh)) {
          const match = fresh.find(c =>
            (leadDigits && (c.phone || "").replace(/\D/g, "") === leadDigits) ||
            (convertingLead.email && c.email?.toLowerCase() === convertingLead.email.toLowerCase())
          );
          newClientId = match?.id ?? null;
        }
        try {
          await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${convertingLead.id}`,{
            method:"PATCH",
            headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
            body:JSON.stringify({ status:"converted", converted_client_id:newClientId, updated_at:new Date().toISOString() }),
          });
          const freshLeads = await fetch(`${SUPABASE_URL}/rest/v1/leads?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
          if (Array.isArray(freshLeads)) setLeads(freshLeads);
        } catch(e) { /* the client was created either way — don't block the flow */ }
      }
    } catch(e) { showToast("Error saving client — check Supabase connection", "error"); }
    setLoading(false);
    setModal(null);
    setEditTarget(null);
    setConvertingLead(null);
  }

  // Client deletion removed from UI — use Supabase SQL to reset test data

  async function handleToggleActive(id) {
    const c = clients.find(c=>c.id===id);
    await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({ active: !c.active })});
    setClients(cs => cs.map(c => c.id===id ? { ...c, active: !c.active } : c));
  }

  async function handleGenerateJobs(year, month, force=false) {
    setJobsLoading(true);
    try {
      const res = await sb.fn("generate-monthly-jobs", { year, month, force });
      if (res.error) { showToast(res.error, "error"); }
      else {
        showToast(res.message || "Jobs generated");
        // S6: the function returns the skipped list under `skipped_clients`
        // (not `no_service_day`), so this warning never fired before.
        if (res.skipped_clients?.length > 0) {
          showToast(`⚠️ ${res.skipped_clients.length} client(s) skipped — no service day: ${res.skipped_clients.slice(0,3).join(", ")}${res.skipped_clients.length>3?"...":""}`, "warning");
        }
        await refreshData(true);
        // C5: refreshData only reloads the base current+next window. If jobs were
        // generated for a month outside it, fetch + merge that month so the grid
        // the operator is looking at actually updates.
        const gStart = new Date(year, month, 1), gEnd = new Date(year, month+1, 0);
        await loadJobsForRange(localDateStr(gStart), localDateStr(gEnd));
      }
    } catch(e) { showToast("Error generating jobs","error"); }
    setJobsLoading(false);
  }

  async function handleBulkAssign(clientIds, techId) {
    const tech = techId ? technicians.find(t=>t.id===techId) : null;
    const todayStr = localDateStr();
    const body = JSON.stringify({technician_id:techId||null,technician_name:tech?.name||null});
    for (const id of clientIds) {
      await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${id}`,{
        method:"PATCH",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
        body,
      });
      // A2: cascade to existing future scheduled jobs (matching handleAssignTech),
      // or this month's already-generated jobs stay in the Unassigned bucket and
      // the tech never sees them.
      await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${id}&scheduled_date=gte.${todayStr}&status=eq.scheduled`,{
        method:"PATCH",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
        body,
      });
    }
    await supabase_refresh();
    showToast(`${clientIds.length} client${clientIds.length!==1?"s":""} ${techId?"assigned to "+tech?.name:"unassigned"} · upcoming visits updated`);
  }

  async function handleAssignTech(clientId, techId, techName) {
    try {
      // 1. Update the client's default tech (drives all FUTURE job-gen runs)
      await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${clientId}`, {
        method: "PATCH",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ technician_id: techId||null, technician_name: techName||null }),
      });

      // 2. Cascade to already-existing future jobs.
      //    Only touches 'scheduled' jobs from today onward — past/completed/
      //    started/skipped jobs are historical records of who actually did
      //    the work and shouldn't be rewritten retroactively.
      const todayStr = localDateStr();
      const cascadeRes = await fetch(
        `${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${clientId}&scheduled_date=gte.${todayStr}&status=eq.scheduled&select=id`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, Prefer: "count=exact" } }
      );
      const affectedJobs = await cascadeRes.json().catch(() => []);
      const affectedCount = Array.isArray(affectedJobs) ? affectedJobs.length : 0;
      if (affectedCount > 0) {
        await fetch(
          `${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${clientId}&scheduled_date=gte.${todayStr}&status=eq.scheduled`,
          {
            method: "PATCH",
            headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({ technician_id: techId||null, technician_name: techName||null }),
          }
        );
      }

      // 3. History — include count so the audit trail shows what propagated
      const description = techId
        ? `Assigned to technician: ${techName}${affectedCount > 0 ? ` (${affectedCount} future scheduled visit${affectedCount===1?"":"s"} also reassigned)` : ""}`
        : `Technician unassigned${affectedCount > 0 ? ` (${affectedCount} future scheduled visit${affectedCount===1?"":"s"} also unassigned)` : ""}`;
      await fetch(`${SUPABASE_URL}/rest/v1/client_history`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, event_type: "tech_assigned", description, new_value: techName||"" }),
      });

      await refreshData(true);
      const toastSuffix = affectedCount > 0 ? ` · ${affectedCount} upcoming visit${affectedCount===1?"":"s"} updated` : "";
      showToast((techId ? `Assigned to ${techName}` : "Technician unassigned") + toastSuffix);
    } catch(e) { showToast("Error assigning technician","error"); console.error(e); }
  }

  async function handleVoidInvoice(inv) {
    if (!window.confirm(`Void the ${inv.invoice_number||inv.date||""} invoice? This cannot be undone.`)) return;

    // If Stripe-native invoice, void in Stripe first
    if (inv.stripe_invoice_id) {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-invoice`,{
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify({ action:"void", stripe_invoice_id:inv.stripe_invoice_id }),
      }).then(r=>r.json());
      if (res.error) { showToast("Stripe void failed: "+res.error,"error"); return; }
    }

    // Update DB
    await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${inv.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({status:"void"}),
    });
    await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:inv.client_id,event_type:"invoice_voided",description:`Invoice ${inv.invoice_number||inv.date||""} voided`.trim(),new_value:"void"})});
    setInvoices(invs=>invs.map(i=>i.id===inv.id?{...i,status:"void"}:i));
    showToast(`Invoice voided`);
  }

  async function handleRetryCharge(inv) {
    if (!window.confirm(`Retry charge of $${Number(inv.amount).toFixed(2)} for ${inv.client_name}?`)) return;
    setLoading(true);
    try {
      if (inv.stripe_invoice_id) {
        // Stripe-native — use finalize-invoice which calls stripe.invoices.pay()
        const res = await fetch(`${SUPABASE_URL}/functions/v1/finalize-invoice`,{
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
          body:JSON.stringify({ stripe_invoice_id: inv.stripe_invoice_id, client_id: inv.client_id })
        }).then(r=>r.json());
        if (res.error) { showToast(res.error,"error"); }
        else { showToast(`Retry successful — $${Number(inv.amount).toFixed(2)} charged!`); }
      } else {
        // Legacy — use charge-first-visit
        const res = await sb.fn("charge-first-visit", {
          invoice_id:         inv.id,
          client_id:          inv.client_id,
          amount:             inv.amount,
          stripe_customer_id: inv.stripe_customer_id,
          description:        `Retry: ${inv.invoice_number}`,
        });
        if (res.error) { showToast(res.error,"error"); }
        else { showToast(`Retry successful — $${Number(inv.amount).toFixed(2)} charged!`); }
      }
      const fresh = await fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(fresh)) setInvoices(fresh);
    } catch(e) { showToast("Retry failed","error"); }
    setLoading(false);
  }

  async function handleEditInvoice(updated) {
    try {
      if (updated.stripe_invoice_id) {
        // Stripe-native — Stripe is source of truth. Send the per-line
        // breakdown so the edge function can target each Stripe
        // invoiceItem precisely (cleanup → cleanup item, deo → deo
        // item, new lines → create, removed lines → delete).
        const res = await fetch(`${SUPABASE_URL}/functions/v1/update-invoice`, {
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
          body:JSON.stringify({
            invoice_id:        updated.id,
            stripe_invoice_id: updated.stripe_invoice_id,
            line_items:        updated.line_items, // NEW — per-Stripe-item edits
            notes:             updated.notes,
            description:       updated.notes,
          }),
        }).then(r=>r.json());
        if (res.error) { showToast(res.error,"error"); return; }
        // update-invoice returns the canonical amount + line_items from
        // Stripe after the update lands. Mirror those into local state.
        const newAmount = res.amount ?? Number(updated.amount);
        const newLineItems = res.line_items ?? updated.line_items;
        setInvoices(invs => invs.map(i => i.id===updated.id
          ? {...i,...updated,amount:newAmount,amount_due:newAmount,amount_remaining:newAmount,line_items:newLineItems}
          : i
        ));
        showToast(`Invoice updated — $${newAmount.toFixed(2)}`);
      } else {
        // Legacy — DB only
        await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${updated.id}`,{
          method:"PATCH",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
          body:JSON.stringify({
            client_name:    updated.client_name,
            invoice_number: updated.invoice_number,
            amount:         updated.amount,
            date:           updated.date,
            due_date:       updated.due_date,
            frequency:      updated.frequency,
            dogs:           updated.dogs,
            status:         updated.status,
            notes:          updated.notes,
            credits:        updated.credits||"[]",
          }),
        });
        setInvoices(invs => [...invs.map(i => i.id===updated.id ? {...i,...updated} : i)]);
        showToast("Invoice updated");
      }
    } catch(e) { showToast("Error updating invoice","error"); }
  }

  async function handleApproveCharge(inv) {
    setLoading(true);
    try {
      const freshInv = await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${inv.id}`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json()).then(rows=>rows[0]);
      if (!freshInv) { showToast("Invoice not found","error"); setLoading(false); return; }
      const chargeAmount = Number(freshInv.amount_due||freshInv.amount);
      if (!chargeAmount || chargeAmount <= 0) { showToast(`Invoice amount is $${chargeAmount} — please edit before charging`, "error"); setLoading(false); return; }
      if (!window.confirm(`Finalize and charge ${freshInv.client_name} $${chargeAmount.toFixed(2)} now?`)) { setLoading(false); return; }

      if (freshInv.stripe_invoice_id) {
        // V8 — Stripe-native: finalize then pay the draft invoice
        const res = await fetch(`${SUPABASE_URL}/functions/v1/finalize-invoice`,{
          method:"POST",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
          body:JSON.stringify({ stripe_invoice_id: freshInv.stripe_invoice_id, client_id: freshInv.client_id })
        }).then(r=>r.json());
        if (res.error) { showToast(res.error,"error"); }
        else {
          showToast(`Charged $${chargeAmount.toFixed(2)} successfully!`);
          await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${freshInv.client_id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({needs_proration:false})});
        }
      } else {
        // Legacy — charge via charge-first-visit
        const res = await sb.fn('charge-first-visit', { invoice_id: freshInv.id, client_id: freshInv.client_id, amount: chargeAmount, stripe_customer_id: freshInv.stripe_customer_id||null, description: freshInv.notes });
        if (res.error) { showToast(res.error,"error"); }
        else {
          showToast(`Charged $${chargeAmount.toFixed(2)} successfully!`);
          await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${freshInv.client_id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({needs_proration:false})});
        }
      }
      const fresh = await fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(fresh)) setInvoices(fresh);
    } catch(e) { showToast('Charge failed — check Stripe dashboard','error'); }
    setLoading(false);
  }

  async function handleDismissInvoice(id) {
    await fetch(`${SUPABASE_URL}/rest/v1/invoices?id=eq.${id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({ status: "open" })});
    setInvoices(inv => inv.map(i => i.id === id ? { ...i, status: "open" } : i));
    showToast('Moved to pending invoices');
  }

  async function handleChargeAll() {
    setLoading(true);
    try {
      const res = await sb.fn("charge-monthly", {});
      showToast(`Charged ${res.charged ?? 0} clients successfully`);
      const inv = await fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      if (Array.isArray(inv)) setInvoices(inv);
    } catch(e) { showToast("Error running charges", "error"); }
    setLoading(false);
  }

  async function handleStatusChange(clientId, action, pauseReason) {
    setLoading(true);
    try {
      const res = await sb.fn("update-subscription", { action, client_id: clientId, pause_reason: pauseReason||null });
      if (res.error) { showToast(res.error, "error"); }
      else {
        showToast(res.message || "Status updated");
        const fresh = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
        if (Array.isArray(fresh)) {
          setClients(fresh);
          if (profileClient && profileClient.id === clientId) {
            setProfileClient(fresh.find(c=>c.id===clientId) || null);
          }
        }
      }
    } catch(e) { showToast("Error updating status","error"); }
    setLoading(false);
  }

  // Generic dispatcher for the five Deodorizer lifecycle actions:
  //   add_deo, pause_deo, resume_deo, cancel_deo, change_deo_frequency
  // All go through update-subscription. After the call returns, refresh
  // the client row in local state so the UI flips immediately (the
  // Stripe webhook will also fire and update the DB independently —
  // both arrive at the same state).
  async function handleDeoAction(action, payload) {
    setLoading(true);
    try {
      const res = await sb.fn("update-subscription", { action, ...payload });
      if (res.error) { showToast(res.error, "error"); }
      else {
        showToast(res.message || "Deodorizer updated");
        // Pull the fresh client row so deo_status / deo_stripe_* are up-to-date
        const fresh = await fetch(
          `${SUPABASE_URL}/rest/v1/clients?id=eq.${payload.client_id}&limit=1`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }
        ).then(r => r.json());
        if (Array.isArray(fresh) && fresh[0]) {
          setClients(cs => cs.map(c => c.id === payload.client_id ? fresh[0] : c));
          if (profileClient?.id === payload.client_id) setProfileClient(fresh[0]);
        }
      }
    } catch(e) { showToast("Error updating deodorizer", "error"); }
    setLoading(false);
  }

  // Re-read the client list after an in-place edit and push the fresh row into BOTH the
  // list and the OPEN profile. Without the setProfileClient half, an edit made from the
  // profile (e.g. the service-day override) saves correctly but the panel keeps rendering
  // the stale prop until a full reload — which reads as "it didn't work".
  async function refreshClientRow(clientId) {
    try {
      const fresh = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,
        { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }).then(r=>r.json());
      if (Array.isArray(fresh)) {
        setClients(fresh);
        if (profileClient?.id === clientId) setProfileClient(fresh.find(c=>c.id===clientId) || profileClient);
      }
    } catch(e) { console.warn("client refresh failed", e); }
    refreshData(true);   // jobs moved too — keep Schedule/Route in step
  }

  async function handleScheduleChange(clientId, newFrequency, newDogs, oldClient, deoEnabled, deoFrequency, pricingOverride) {
    setLoading(true);
    try {
      // Payment pending clients — update plan directly, create Stripe draft proration invoice
      if (oldClient.status === "payment_pending") {
        // A bulk-imported client has a CRM row but no Stripe customer/subscription
        // yet. Set billing up in place via create-customer's adopt mode (client_id)
        // so the operator can then send a payment link — instead of the old dead-end
        // that told them to recreate the profile. A normal payment_pending client
        // (added via Add Client, already has a Stripe customer + incomplete sub)
        // takes the re-price path below.
        if (!oldClient.stripe_customer_id) {
          const res = await sb.fn("create-customer", {
            client_id:     clientId,
            frequency:     newFrequency,
            dogs:          newDogs,
            deo_enabled:   deoEnabled || false,
            deo_frequency: deoFrequency || null,
            start_date:    oldClient.start_date || undefined,
          });
          if (res?.error) { showToast(res.error, "error"); setLoading(false); return; }
          showToast(`Billing set up — now send the payment link to activate.`);
          await supabase_refresh();
          setLoading(false);
          return;
        }

        // Update client plan
        await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${clientId}`, {
          method: "PATCH",
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
          body: JSON.stringify({ frequency: newFrequency, dogs: newDogs, deo_enabled: deoEnabled||false, deo_frequency: deoFrequency||null }),
        });

        // Update Stripe subscription price to match new plan
        await sb.fn("update-subscription", {
          action:        "change_price",
          client_id:     clientId,
          new_amount:    getPrice(newFrequency, newDogs, pricingOverride),
        });

        // Log history
        await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:clientId,event_type:"frequency_changed",description:`Plan set: ${newFrequency}, ${newDogs} dog${newDogs>1?"s":""}${deoEnabled?` + ${deoFrequency} deodorizer`:""} @ $${getPrice(newFrequency,newDogs,pricingOverride).toFixed(2)}/mo`,new_value:newFrequency})});
        showToast(`Plan saved! Send payment link to activate.`);
        await supabase_refresh();
      } else {
        // Active clients — schedule change via edge function
        const res = await sb.fn("update-subscription", {
          action: "schedule_change",
          client_id: clientId,
          new_frequency: newFrequency,
          new_dogs: newDogs,
        });
        if (res.error) { showToast(res.error, "error"); }
        else {
          showToast(`Change scheduled for ${res.change_date}`);
          await new Promise(r => setTimeout(r, 1000));
          await supabase_refresh();
        }
      }
    } catch(e) { console.error(e); showToast("Error saving plan","error"); }
    setLoading(false);
  }

  async function handleContactUpdate(clientId, newData, oldData) {
    setLoading(true);
    try {
      // Save referred_by directly to DB (not via edge function)
      if (newData.referred_by !== oldData.referred_by) {
        await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${clientId}`,{
          method:"PATCH",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
          body:JSON.stringify({referred_by:newData.referred_by||null}),
        });
      }
      const res = await sb.fn("update-subscription", {
        action: "update_contact",
        client_id: clientId,
        ...newData,
        old_values: oldData,
      });
      if (res.error) { showToast(res.error, "error"); }
      else {
        // If address or zip changed, clear saved coords so map re-geocodes
        if (newData.address !== oldData.address || newData.zip !== oldData.zip) {
          await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${clientId}`, {
            method: "PATCH",
            headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
            body: JSON.stringify({ lat: null, lng: null }),
          });
        }
        showToast("Contact info updated");
        await supabase_refresh();
      }
    } catch(e) { showToast("Error updating contact","error"); }
    setLoading(false);
  }

  async function supabase_refresh() {
    const freshClients = await fetch(`${SUPABASE_URL}/rest/v1/clients?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
    const freshInvoices = await fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
    if (Array.isArray(freshClients)) {
      setClients(freshClients);
      if (profileClient) {
        const updated = freshClients.find(c=>c.id===profileClient.id);
        if (updated) setProfileClient(updated);
      }
    }
    if (Array.isArray(freshInvoices)) setInvoices(freshInvoices);
  }

  async function handleLogout() {
    await sbAuth.signOut();
    setSession(null);
    setClients([]);
    setInvoices([]);
    setPage("dashboard");
  }

  // Show login screen if not authenticated
  if (!authReady) return (
    <>
      <style>{globalCss}</style>
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#f5f6f8"}}>
        <div style={{textAlign:"center",color:"#8993a4",fontSize:13}}>Loading...</div>
      </div>
    </>
  );

  if (!session) return (
    <>
      <style>{globalCss}</style>
      <AuthPage onLogin={s=>{ setSession(s); setUserRole(s.role||"owner"); setUserTechId(s.technician_id||null); refreshData(); fetchOrg(); }} />
    </>
  );

  // Technician view — only show My Route
  if (userRole === "technician") return (
    <>
      <style>{globalCss}</style>
      <TechView techId={userTechId} technicians={technicians} clients={clients} pricing={pricing} onLogout={handleLogout} orgName={org?.name} />
    </>
  );

  // Operator onboarding wizard (Phase C) — owner whose company isn't set up yet
  if (userRole === "owner" && org && org.onboarding_completed === false) return (
    <>
      <style>{globalCss}</style>
      <OnboardingWizard
        org={org}
        serviceArea={serviceArea}
        technicians={technicians}
        clients={clients}
        showToast={showToast}
        onRefresh={()=>{ refreshData(true); refreshJobsDone(); }}
        onComplete={fetchOrg}
      />
    </>
  );

  // Manual jobs (source='manual') are billed at CREATION, not completion.
  // Completing one is a status-only action, identical to any other job — kept as
  // its own handler only because the route/schedule surfaces wire it as a prop.
  async function handleCompleteManualJob(job) {
    const jobDate = job.scheduled_date || job.date;
    await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${job.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({ status:"completed", completed_at:new Date().toISOString() }),
    });
    await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:job.client_id,event_type:"manual_job",description:`One-time job completed on ${jobDate}`,new_value:job.notes||""})});
    await refreshData(true);
    refreshJobsDone();
    showToast("Manual job completed");
  }

  // Block adding clients until Stripe is connected — otherwise the client's Stripe
  // customer is created on the PLATFORM account and orphaned (payments would route to
  // the platform, not this operator). Mirrors the create-customer backend guard.
  const openAddClient = () => {
    if (!org?.stripe_account_id) {
      showToast("Connect your Stripe account first — Settings → Payments — before adding clients.", "error");
      setPage("settings");
      return;
    }
    setConvertingLead(null);   // a plain Add Client must never inherit a lead prefill
    setEditTarget(null);
    setModal("client");
  };

  // Leads are read/written via RLS (scoped to the operator's org by the JWT).
  // PATCH the status (+ updated_at) then refresh so the badge/tab counts re-derive.
  async function updateLeadStatus(id, status) {
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${id}`,{
        method:"PATCH",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
        body:JSON.stringify({ status, updated_at:new Date().toISOString() }),
      });
      setLeads(ls => ls.map(l => l.id===id ? { ...l, status } : l));
      showToast("Lead updated");
    } catch(e) { showToast("Error updating lead","error"); }
  }

  // Convert a lead → open the New-client modal pre-populated from the lead.
  // Same Stripe guard as Add Client (create-customer needs the connected
  // account or the customer is orphaned on the platform). On a successful
  // save, handleSaveClient marks the lead converted + links the new client.
  function handleConvertLead(lead) {
    if (!org?.stripe_account_id) {
      showToast("Connect your Stripe account first — Settings → Payments — before converting leads.", "error");
      setPage("settings");
      return;
    }
    setEditTarget(null);
    setConvertingLead(lead);
    setModal("client");
  }

  const pages = {
    dashboard: <Dashboard clients={clients} invoices={invoices} todayJobs={todayJobs} jobsDone={jobsDone} pricing={pricing} toggleJob={toggleJob} setPage={setPage} mrr={mrr} org={org} serviceArea={serviceArea} onSetup={(tab,sub)=>{ setSettingsJump({ tab, sub, t: Date.now() }); setPage("settings"); }} />,
    clients:   <Clients   clients={clients} onAdd={openAddClient} onEdit={c=>{setEditTarget(c);setModal("client");}} onToggleActive={handleToggleActive} onViewProfile={c=>{setProfileClient(c);setPage("profile");syncOpenInvoices(c.id);}} technicians={technicians} onBulkAssign={handleBulkAssign} pricing={pricing} showToast={showToast} />,
    requests:  <RequestsPage requests={requests} clients={clients} onRefresh={()=>refreshData(true)} showToast={showToast} />,
    platform:  isPlatformAdmin ? <PlatformPage showToast={showToast} /> : null,
    // Phase 6-8 operator surfaces. Each is gated on the same per-org flag the customer
    // app reads, so a page can never offer to manage something customers cannot see.
    deliveries: <DeliveriesPage showToast={showToast} />,
    moderation: <ModerationPage showToast={showToast} isPlatformAdmin={isPlatformAdmin} />,
    picks:      isPlatformAdmin ? <PicksPage showToast={showToast} /> : null,
    leads:     <Leads     leads={leads} onUpdateStatus={updateLeadStatus} onConvertLead={handleConvertLead} showToast={showToast} />,
    schedule:  <Schedule  allJobs={allJobs} jobs={jobs} pricing={pricing} jobsDone={jobsDone} toggleJob={toggleJob} weekOffset={weekOffset} setWeekOffset={setWeekOffset} today={today} manualJobs={manualJobs} onAddManualJob={()=>{setManualJobClient(null);setShowManualJob(true);}} onCompleteManualJob={handleCompleteManualJob} technicians={technicians} clients={clients} invoices={invoices} serviceArea={serviceArea} zipSecondDay={zipSecondDay} onOpenClient={c=>{setProfileClient(c);setPage("profile");syncOpenInvoices(c.id);}} onFixServiceArea={()=>{ setSettingsJump({ tab:"team", sub:"area", t: Date.now() }); setPage("settings"); }} onRefresh={()=>{ refreshData(true); refreshJobsDone(); }} showToast={showToast} onGenerateJobs={handleGenerateJobs} jobsLoading={jobsLoading} monthOffset={scheduleMonthOffset} onMonthOffsetChange={setScheduleMonthOffset} />,
    route:     <Route     todayJobs={todayJobs} jobsDone={jobsDone} toggleJob={toggleJob} manualJobs={manualJobs.filter(j=>j.date===todayStr&&j.status==="scheduled")} onCompleteManualJob={handleCompleteManualJob} technicians={technicians} clients={clients} onRefreshClients={(fresh)=>setClients(fresh)} setPage={setPage} pricing={pricing} showToast={showToast} onRefresh={()=>{ refreshData(true); refreshJobsDone(); }} />,
    invoices:  <Invoices  invoices={invoices} onChargeAll={handleChargeAll} onApprove={handleApproveCharge} onDismiss={handleDismissInvoice} onEdit={handleEditInvoice} onRetryCharge={handleRetryCharge} onIssueRefund={inv=>setShowRefundModal(inv)} onVoidInvoice={handleVoidInvoice} />,
    clientmap: <ClientMap clients={clients} hovered={hovered} setHovered={setHovered} mapFilter={mapFilter} setMapFilter={setMapFilter} serviceArea={serviceArea} setPage={setPage} technicians={technicians} pricing={pricing} showToast={showToast} onAssignTech={handleAssignTech} onViewProfile={c=>{setProfileClient(c);setPage("profile");syncOpenInvoices(c.id);}} />,
    reports:   <ReportsPage invoices={invoices} refunds={refunds} credits={credits} technicians={technicians} clients={clients} pricing={pricing} showToast={showToast} onRefreshInvoices={()=>refreshData(true)} />,
    settings:  <SettingsPage technicians={technicians} serviceArea={serviceArea} zipSecondDay={zipSecondDay} onRefresh={()=>{ refreshData(true); refreshJobsDone(); }} showToast={showToast} onGenerateJobs={handleGenerateJobs} jobsLoading={jobsLoading} clients={clients} ownerEmail={session?.user?.email} org={org} onOrgSaved={fetchOrg} jump={settingsJump} role={userRole} />,
    techs:     <TechProfilesPage technicians={technicians} clients={clients} invoices={invoices} pricing={pricing} dbVehicles={dbVehicles} dbTrips={dbTrips} showToast={showToast} onRefresh={()=>{ refreshData(true); refreshJobsDone(); }} onRefreshVehicles={()=>fetch(`${SUPABASE_URL}/rest/v1/vehicles?order=nickname.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(v=>{if(Array.isArray(v))setDbVehicles(v);})} />,
    fleet:     <FleetPage technicians={technicians} dbVehicles={dbVehicles} showToast={showToast} onRefreshVehicles={()=>fetch(`${SUPABASE_URL}/rest/v1/vehicles?order=nickname.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(v=>{if(Array.isArray(v))setDbVehicles(v);})} />,
    profile:   profileClient ? <ClientProfile pricing={pricing} orgName={org?.name} client={profileClient} invoices={invoices.filter(i=>i.client_id===profileClient.id)} onBack={()=>{setProfileClient(null);setPage('clients');}} onStatusChange={handleStatusChange} onScheduleChange={handleScheduleChange} onContactUpdate={handleContactUpdate} onClientRefreshed={refreshClientRow} onDeoAction={handleDeoAction} zipSecondDay={zipSecondDay} showToast={showToast} onRefreshInvoices={()=>fetch(`${SUPABASE_URL}/rest/v1/invoices?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(inv=>{if(Array.isArray(inv))setInvoices(inv);})} onAddManualJob={(c)=>{setManualJobClient(c);setShowManualJob(true);}} technicians={technicians} onAssignTech={handleAssignTech} onApprove={handleApproveCharge} onRetryCharge={handleRetryCharge} onIssueRefund={inv=>setShowRefundModal(inv)} onVoidInvoice={handleVoidInvoice} onEditInvoice={handleEditInvoice} credits={credits} onRefreshCredits={()=>fetch(`${SUPABASE_URL}/rest/v1/credits?order=created_at.desc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(cr=>{if(Array.isArray(cr))setCredits(cr);})} /> : null,
  };

  const guideTexting = () => { if (showToast) showToast("Finish setting up texting to call or message clients.", "info"); setSettingsJump({ tab: "texting", t: Date.now() }); setPage("settings"); };
  const guideFleet = () => { if (showToast) showToast("Connect Bouncie to use Fleet for live vehicle tracking.", "info"); setSettingsJump({ tab: "fleet", t: Date.now() }); setPage("settings"); };
  const commsValue = { textingReady: comms.textingReady, fleetReady: comms.fleetReady, number: comms.number, guideTexting, guideFleet, requireTexting: () => { if (comms.textingReady) return true; guideTexting(); return false; } };

  return (
    <CommsContext.Provider value={commsValue}>
      <style>{globalCss}</style>
      <div className="app">
        {/* Sidebar */}
        <div className={`sidebar-backdrop${sidebarOpen?" open":""}`} onClick={()=>setSidebarOpen(false)}/>
        <aside className={`sidebar${sidebarOpen?" open":""}`}>
          <div className="sidebar-logo" style={{textAlign:"center"}}>
            <h1 style={{textAlign:"center"}}><ScoopMark size={28} style={{verticalAlign:"middle",marginRight:9}} />iScoop<span>CRM</span></h1>
            <p>Your business, handled.</p>
          </div>
          <nav className="nav">
            <div className="nav-section">Main</div>
            {[
              {key:"dashboard", label:"Dashboard",     icon:"dashboard"},
              {key:"clients",   label:"Clients",        icon:"clients",  badge:clients.filter(c=>!c.technician_id&&c.active).length||null},
              {key:"leads",     label:"Leads",          icon:"clients",  badge:leads.filter(l=>l.status==="new").length||null},
              {key:"requests",  label:"Requests",       icon:"bell",     badge:requests.filter(r=>r.status==="pending"||!r.seen_at).length||null, badgeRed:true},
              // Only when this operator actually runs the feature — the same flags the
              // customer app reads (sql/077, sql/078).
              ...(org?.allow_deliveries ? [{key:"deliveries", label:"Deliveries", icon:"clients"}] : []),
              ...(org?.allow_community  ? [{key:"moderation", label:"Posts & reports", icon:"bell"}] : []),
              {key:"schedule",  label:"Schedule",       icon:"schedule"},
              {key:"route",     label:"Today's Route",  icon:"map",      badge:new Set(todayJobs.filter(j=>j.source!=="manual").map(j=>(j.technician_id||"none")+"|"+j.client_id)).size||null},
              {key:"clientmap", label:"Client Map",     icon:"map"},
              {key:"invoices",  label:"Invoices",       icon:"invoice",  badge:invoices.filter(i=>i.status==="draft"||i.status==="uncollectible").length||null, badgeRed:true},
              {key:"reports",   label:"Reports",        icon:"analytics"},
              {key:"techs",     label:"Technicians",    icon:"clients"},
              {key:"fleet",     label:"Fleet",          icon:"map"},
              {key:"settings",  label:"Settings",       icon:"settings"},
            ].map(({key,label,icon,badge,badgeRed})=>(
              <div key={key} className={`nav-item${page===key?" active":""}`} onClick={()=>{setPage(key);setSidebarOpen(false);}}>
                <Icon n={icon} size={15}/>{label}
                {badge ? <span className={`nav-badge${badgeRed?" red":""}`}>{badge}</span> : null}
              </div>
            ))}
            {/* The platform owner's ecosystem view. Its own section, because it spans
                every operator — unlike everything above, which is scoped to this org. */}
            {isPlatformAdmin ? (
              <>
                <div className="nav-section">Platform</div>
                <div className={`nav-item${page==="platform"?" active":""}`} onClick={()=>{setPage("platform");setSidebarOpen(false);}}>
                  <Icon n="analytics" size={15}/>All operators
                </div>
                <div className={`nav-item${page==="picks"?" active":""}`} onClick={()=>{setPage("picks");setSidebarOpen(false);}}>
                  <Icon n="clients" size={15}/>Shop picks
                </div>
              </>
            ) : null}
          </nav>
          <div className="sidebar-footer" style={{display:"flex",flexDirection:"column",gap:8}}>
            <div>iScoopCRM v7.0</div>
            <button onClick={handleLogout} className="signout-btn" style={{background:"rgba(255,255,255,.08)",border:"1px solid rgba(255,255,255,.12)",color:"rgba(255,255,255,.5)",borderRadius:5,padding:"5px 10px",cursor:"pointer",fontSize:11,fontFamily:"Inter,sans-serif",textAlign:"left",transition:".12s"}}
              onMouseEnter={e=>e.target.style.background="rgba(255,255,255,.15)"}
              onMouseLeave={e=>e.target.style.background="rgba(255,255,255,.08)"}>
              Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="main">
          <div className="topbar">
            <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
              <button className="sidebar-toggle" aria-label="Open menu" onClick={()=>setSidebarOpen(true)}>☰</button>
              <h2>{page==="platform"?"All operators":page==="picks"?"Shop picks":page==="moderation"?"Posts & reports":page==="deliveries"?"Deliveries":page==="route"?"Today's Route":page==="clientmap"?"Client Map":page==="profile"?(profileClient?.name||"Client Profile"):page==="settings"?"Settings":page==="reports"?"Reports":page.charAt(0).toUpperCase()+page.slice(1)}</h2>
            </div>
            <div className="topbar-actions">
              {page==="clients"  && <button className="btn btn-gold" onClick={openAddClient}><Icon n="plus" size={14}/>Add Client</button>}
              {page==="invoices" && <span className="topbar-note" style={{fontSize:11,color:"#8993a4",padding:"4px 8px"}}>✓ Stripe auto-charges on the 1st</span>}
              <span className="topbar-date">{today.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</span>
              <div style={{position:"relative"}}>
                <div className="topbar-icon" style={{position:"relative"}} onClick={()=>setShowNotifs(v=>{ if(!v){ const ids=invoices.filter(i=>i.status==="draft").map(i=>i.id); setReadNotifs(ids); localStorage.setItem("readNotifs",JSON.stringify(ids)); } return !v; })}>
                  <Icon n="bell" size={15}/>
                  {!showNotifs&&invoices.filter(i=>i.status==="draft"&&!readNotifs.includes(i.id)).length>0&&
                    <div className="notif-badge">{invoices.filter(i=>i.status==="draft"&&!readNotifs.includes(i.id)).length}</div>}
                </div>
                {showNotifs&&(
                  <div className="notif-dd" style={{position:"absolute",right:0,top:"calc(100% + 8px)",width:"min(340px, calc(100vw - 24px))",background:"white",borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,.15)",zIndex:999,border:"1px solid #e3e8ef"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:"1px solid #e3e8ef"}}>
                      <span style={{fontWeight:700,fontSize:13,color:"#1a1f36"}}>Notifications</span>
                      <button onClick={()=>{ const ids=invoices.filter(i=>i.status==="draft").map(i=>i.id); setReadNotifs(ids); localStorage.setItem("readNotifs",JSON.stringify(ids)); }} style={{fontSize:11,background:"none",border:"none",cursor:"pointer",color:"#8993a4",padding:"8px 10px",minHeight:36}}>Mark all read</button>
                    </div>
                    <div style={{maxHeight:320,overflowY:"auto"}}>
                      {invoices.filter(i=>i.status==="draft").length===0&&(
                        <div style={{padding:"20px 16px",textAlign:"center",fontSize:12,color:"#8993a4"}}>No notifications</div>
                      )}
                      {invoices.filter(i=>i.status==="draft").map(inv=>(
                        <div key={inv.id} onClick={()=>{ setPage("invoices"); setShowNotifs(false); setReadNotifs(r=>{const n=[...r,inv.id];localStorage.setItem("readNotifs",JSON.stringify(n));return n;}); }}
                          style={{padding:"10px 16px",borderBottom:"1px solid #f4f5f7",cursor:"pointer",background:readNotifs.includes(inv.id)?"#fff":"#f8f9ff",display:"flex",gap:10,alignItems:"flex-start"}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:readNotifs.includes(inv.id)?"transparent":"#0052cc",flexShrink:0,marginTop:4}}/>
                          <div>
                            <div style={{fontSize:12,fontWeight:600,color:"#1a1f36"}}>Invoice needs approval</div>
                            <div style={{fontSize:11,color:"#42526e",marginTop:2}}>{inv.client_name} · {inv.invoice_number} · <strong style={{color:NAVY}}>${Number(inv.amount).toFixed(2)}</strong></div>
                            <div style={{fontSize:10,color:"#8993a4",marginTop:1}}>{inv.date}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="topbar-avatar">A</div>
            </div>
          </div>

          {loading && <div style={{padding:"10px 28px"}}><div className="alert alert-info">⏳ Syncing with database...</div></div>}
          {toast   && <div className="toast-wrap" style={{padding:"0 28px"}}><div className={`alert alert-${toast.type}`}>{toast.type==="success"?"✅":toast.type==="error"?"❌":"ℹ️"} {toast.msg}</div></div>}

          {/* z 9: under the sticky topbar (z 10 desktop / 60 phone) so the dropdown inside it stays tappable — at 998 it painted OVER the dropdown */}
          {showNotifs&&<div style={{position:"fixed",inset:0,zIndex:9}} onClick={()=>setShowNotifs(false)}/>}
          <div className="content">{pages[page]}</div>
        </div>
      </div>

      <Softphone />

      {modal==="client" && (
        <ClientModal
          client={editTarget}
          prefill={convertingLead ? {
            first_name:    convertingLead.first_name,
            phone:         convertingLead.phone,
            address:       convertingLead.address,
            city:          convertingLead.city,
            state:         convertingLead.state,
            zip:           convertingLead.zip,
            lat:           convertingLead.lat,
            lng:           convertingLead.lng,
            dogs:          convertingLead.dogs,
            frequency:     convertingLead.frequency,
            deo_enabled:   convertingLead.deo_enabled,
            deo_frequency: convertingLead.deo_frequency,
          } : null}
          onSave={handleSaveClient}
          onClose={()=>{setModal(null);setEditTarget(null);setConvertingLead(null);}}
          showToast={showToast}
          clients={clients}
          pricing={pricing}
          zipSecondDay={zipSecondDay}
          stripeAccountId={org?.stripe_account_id || null}
        />
      )}
      {showRefundModal && (
        <RefundModal
          invoice={showRefundModal}
          onClose={()=>setShowRefundModal(null)}
          onSuccess={async()=>{
            setShowRefundModal(null);
            await refreshData(true);
            showToast("Refund issued successfully");
          }}
          showToast={showToast}
        />
      )}
      {showManualJob && (
        <ManualJobModal
          clients={clients.filter(c=>c.active)}
          preselectedClient={manualJobClient}
          onClose={()=>{setShowManualJob(false);setManualJobClient(null);}}
          onSave={async(job)=>{
            // Manual jobs live in the jobs table (source='manual'). The operator
            // charges the client AT CREATION — charge first, then create the
            // scheduled job stamped charged_at. Completion (by a tech, anywhere)
            // is a status-only action and never bills. D5: inherit
            // address/zip/dogs/technician from the client so the job lands on the
            // right tech's route, not "Unassigned".
            const mc = clients.find(c=>c.id===job.client_id);
            const cost = Number(job.cost)||0;
            // Charge only when there's an amount AND a card on file; with no card
            // the modal already warns the operator to collect payment separately.
            const willCharge = cost > 0 && !!mc?.stripe_pm_last4;
            let charged = false;
            if (willCharge) {
              let res;
              try {
                res = await sb.fn("charge-client", {
                  client_id:       job.client_id,
                  amount:          cost,
                  reason:          "One-time service",
                  notes:           job.notes || `Manual job on ${job.date}`,
                  idempotency_key: `manualjob:${crypto.randomUUID()}`,
                });
              } catch { res = { error: "network error" }; }
              if (!res || res.error) {
                showToast(`Charge failed — job not created: ${res?.error || "unknown error"}`, "error");
                return;
              }
              charged = true;
            }
            const jobRes = await fetch(`${SUPABASE_URL}/rest/v1/jobs`,{
              method:"POST",
              headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
              body:JSON.stringify({
                client_id:       job.client_id,
                client_name:     job.client_name,
                client_address:  mc?.address||null,
                client_zip:      mc?.zip||null,
                dogs:            mc?.dogs||null,
                service_type:    job.service_type||"cleanup",
                technician_id:   mc?.technician_id||null,
                technician_name: mc?.technician_name||null,
                scheduled_date:  job.date,
                status:          "scheduled",
                notes:           job.notes||null,
                cost:            cost,
                time:            job.time||null,
                source:          "manual",
                ...(charged?{charged_at:new Date().toISOString()}:{}),
              }),
            });
            // The charge already went through, so DON'T claim a false success if the
            // job row failed to insert (e.g. a duplicate manual job on the same
            // date/service). Surface it so the operator knows the card was charged
            // but the visit wasn't scheduled.
            if (!jobRes.ok) {
              const detail = await jobRes.text().catch(()=>"");
              showToast(charged
                ? `Card charged $${cost.toFixed(2)} but the job could not be scheduled (it may already exist). Please check before re-charging.`
                : `Could not schedule the job${detail?` (${jobRes.status})`:""}.`, "error");
              return;
            }
            await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:job.client_id,event_type:"manual_job",description:`One-time job scheduled for ${job.date}${charged?" — charged $"+cost.toFixed(2):""}`,new_value:job.notes||""})});
            await refreshData(true);
            setShowManualJob(false);
            setManualJobClient(null);
            showToast(charged
              ? `Manual job scheduled — $${cost.toFixed(2)} charged`
              : (cost>0 ? "Manual job scheduled — no card on file, collect payment separately" : "Manual job scheduled!"));
          }}
        />
      )}
    </CommsContext.Provider>
  );
}
